import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Grid,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SaveIcon from '@mui/icons-material/Save'
import { projectsApi, ProjectCreate, ProjectStatus } from '@shared/api-client'
import { useInstance } from '../contexts/InstanceContext'
import { provisioningApi } from '../services/provisioningApi'
import { normalizeInstanceConfig, createInstanceProject } from '../utils/instanceApi'

const statuses: ProjectStatus[] = ['planning', 'active', 'on_hold', 'completed', 'cancelled']
const priorities = ['low', 'medium', 'high', 'urgent']

export default function AddProject() {
  const navigate = useNavigate()
  const { currentInstance, instances } = useInstance()
  const [loading, setLoading] = useState(false)
  const [selectedInstanceId, setSelectedInstanceId] = useState<string>(currentInstance?.id || '')
  const [formData, setFormData] = useState<ProjectCreate>({
    name: '',
    description: '',
    status: 'planning',
    priority: 'medium',
    start_date: '',
    end_date: '',
    budget: '',
  })

  useEffect(() => {
    if (currentInstance && !selectedInstanceId) {
      setSelectedInstanceId(currentInstance.id)
    }
  }, [currentInstance])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const projectData: ProjectCreate = {
        ...formData,
        start_date: formData.start_date || undefined,
        end_date: formData.end_date || undefined,
        budget: formData.budget || undefined,
        description: formData.description || undefined,
      }

      // If a specific instance is selected, add to that instance
      if (selectedInstanceId && selectedInstanceId !== currentInstance?.id) {
        const instanceData = await provisioningApi.getInstance(selectedInstanceId)
        const instance = normalizeInstanceConfig(instanceData)
        await createInstanceProject(instance, projectData)
        navigate(`/organizations/${selectedInstanceId}`)
      } else {
        // Add to current instance
        const project = await projectsApi.create(projectData)
        navigate(`/projects/${project.id}`)
      }
    } catch (error: any) {
      console.error('Failed to create project:', error)
      alert(error.response?.data?.detail || 'Failed to create project')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (field: keyof ProjectCreate) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [field]: e.target.value })
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/projects')}
        >
          Back to Projects
        </Button>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Add New Project
        </Typography>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {instances.length > 1 && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Organization</InputLabel>
                  <Select
                    value={selectedInstanceId}
                    label="Organization"
                    onChange={(e) => setSelectedInstanceId(e.target.value)}
                  >
                    {instances.map((instance) => (
                      <MenuItem key={instance.id} value={instance.id}>
                        {instance.name} ({(instance as any).organization_domain || (instance as any).organizationDomain})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Project Name"
                value={formData.name}
                onChange={handleChange('name')}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={handleChange('description')}
                multiline
                rows={4}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as ProjectStatus })}
                >
                  {statuses.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  label="Priority"
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                >
                  {priorities.map((priority) => (
                    <MenuItem key={priority} value={priority}>
                      {priority}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={formData.start_date}
                onChange={handleChange('start_date')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={formData.end_date}
                onChange={handleChange('end_date')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Budget"
                type="number"
                value={formData.budget}
                onChange={handleChange('budget')}
                inputProps={{ step: '0.01', min: '0' }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button onClick={() => navigate('/projects')}>Cancel</Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={loading || !formData.name}
                >
                  {loading ? 'Creating...' : 'Create Project'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  )
}

